import cloneDeep from 'lodash/cloneDeep';
import uniqBy from 'lodash/uniqBy';
import { applySnapshot, cast, flow, types } from 'mobx-state-tree';
import moment from 'moment';
import { killNulls, removeHtmlTags, safelyInvoke, splitPhone } from 'utils';
import {
  ApplicantSearchContext,
  ApplicantSearchGuard,
  IArea,
  IHouse,
  IMember,
  INewRequest,
  IPhone,
  IRequest,
  ITenant,
  IThirdPartyTypeGtwy,
  IWorkerNew,
} from 'types';
import { Areas, Requests, Tenants, Workers } from 'http-requests';
import {
  IHistoryTabType,
  TabType,
} from 'views/authorized/pages/requests/tabs/create-request/types';
import { IOtherMemberKeys } from 'components/create-request/tenants/components/other-tenant/types';
import { IApplicantType } from 'components/create-request/types';
import { ISelectValue } from 'components/ui';
import {
  CurrentSelectedCoordinators,
  RequestStateModel,
} from '../general-models';

const { GetFamiliesByArea } = Areas;
const {
  GetTenantsCardsByPhone,
  CreateOtherTenant,
  SetTenantContacts,
  SetEmployeeContacts,
  GetNumberSocialNetworks,
  SetOtherPersonContacts,
  SetOtherEmployeeContacts,
} = Tenants;
const {
  PostShutdownNotification,
  CreateRequest,
  CreateNewOtherProvider,
  CreateNewOtherEmployee,
  GetOtherEmployeeAccounts,
  GetOtherPersonAccounts,
  GetOtherProvidersByPhone,
  CreateNewOtherPerson,
} = Requests;
const { GetWorkersCardsByPhone } = Workers;

const ApplicantTypeMST = types.enumeration('ApplicantType', [
  'tenant',
  'employee',
  'other',
]);

const ThirdPartyTypeMST = types.enumeration('OtherType', [
  'other_person',
  'other_employee',
]);

const TabTypeMST = types.enumeration('TabType', [
  'applicant',
  'status',
  'meters',
  'description',
  'additionally',
]);

const HistoryTabTypeMST = types.enumeration('HistoryTabType', [
  'house',
  'area',
  'stats',
]);

const ApplicantRequestsModel = types.model({
  members: types.optional(RequestStateModel, {}),
  otherMember: types.optional(RequestStateModel, {}),
  workers: types.optional(RequestStateModel, {}),
  thirdPartyAccountsByPhone: types.optional(RequestStateModel, {}),
  otherPersonAccounts: types.optional(RequestStateModel, {}),
  otherEmployeeAccounts: types.optional(RequestStateModel, {}),
  otherProviderAccounts: types.optional(RequestStateModel, {}),
  allThirdPartyAccounts: types.optional(RequestStateModel, {}),
  addContacts: types.optional(RequestStateModel, {}),
});

const RequestsModel = types.model({
  create: types.optional(RequestStateModel, {}),
  socials: types.optional(RequestStateModel, {}),
  areas: types.optional(RequestStateModel, {}),
  addOtherMember: types.optional(RequestStateModel, {}),
  addThirdPartyAccount: types.optional(RequestStateModel, {}),
  addNewOtherProvider: types.optional(RequestStateModel, {}),
  addNewOtherEmployee: types.optional(RequestStateModel, {}),
  addNewOtherPerson: types.optional(RequestStateModel, {}),
  notify: types.optional(RequestStateModel, {}),
});

const HouseModel = types.model({
  address: types.optional(types.string, ''),
  fias_addrobjs: types.optional(types.array(types.string), []),
  id: types.optional(types.string, ''),
});

const AreaModel = types.model({
  house: types.optional(HouseModel, {}),
  id: types.optional(types.string, ''),
  is_shared: types.optional(types.maybeNull(types.boolean), null),
  number: types.optional(types.number, 0),
  str_number: types.optional(types.string, ''),
  str_number_full: types.optional(types.string, ''),
  _type: types.optional(types.array(types.string), []),
});

const PhoneModel = types.model({
  add: types.optional(types.maybeNull(types.string), null),
  added_by_tenant: types.optional(types.boolean, false),
  code: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  id: types.optional(types.maybeNull(types.string), null),
  not_actual: types.optional(types.boolean, false),
  phone_type: types.optional(types.string, 'cell'),
  str_number: types.optional(types.maybeNull(types.string), null),
});

const PhoneModelGtwy = types.model({
  _type: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  add: types.optional(types.maybeNull(types.string), null),
});

const MemberModel = types
  .model({
    assessment: types.optional(
      types.maybeNull(types.frozen<{ [propName: string]: any }>()),
      null
    ),
    can_vote: types.optional(types.boolean, false),
    email: types.optional(types.maybeNull(types.string), ''),
    has_access: types.optional(types.boolean, false),
    id: types.optional(types.string, ''),
    is_archive: types.optional(types.boolean, false),
    is_coop_member: types.optional(types.boolean, false),
    is_owner: types.optional(types.boolean, false),
    is_renter: types.optional(types.boolean, false),
    is_responsible: types.optional(types.boolean, false),
    living: types.optional(types.boolean, false),
    ownership: types.optional(
      types.maybeNull(types.frozen<{ [propName: string]: any }>()),
      null
    ),
    phones: types.optional(types.array(PhoneModel), []),
    property_share: types.optional(types.array(types.number), []),
    registered: types.optional(types.boolean, false),
    str_name: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setPhones(phones: IPhone[]) {
      self.phones = cast(phones);
    },
    setRating(rating: { [key: string]: any }) {
      self.assessment = rating;
      self.can_vote = false;
    },
  }));

const DepartmentModel = types.model({
  name: types.optional(types.string, ''),
  provider: types.optional(types.string, ''),
});

const PositionModel = types.model({
  name: types.optional(types.maybeNull(types.string), ''),
});

const WorkerModel = types
  .model({
    department: types.optional(DepartmentModel, {}),
    email: types.optional(types.maybeNull(types.string), null),
    id: types.optional(types.string, ''),
    inn: types.optional(types.maybeNull(types.string), null),
    number: types.optional(types.string, ''),
    phones: types.optional(types.array(PhoneModel), []),
    position: types.optional(PositionModel, {}),
    short_name: types.optional(types.string, ''),
    str_name: types.optional(types.string, ''),
    _type: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setPhones(phones: IPhone[]) {
      self.phones = cast(phones);
    },
  }));

const ProviderModelGtwy = types.model({
  _id: types.optional(types.string, ''),
  inn: types.optional(types.string, ''),
  ogrn: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
});

const OtherEmployeeModel = types
  .model({
    _id: types.optional(types.string, ''),
    last_name: types.optional(types.string, ''),
    first_name: types.optional(types.string, ''),
    patronymic_name: types.optional(types.maybeNull(types.string), null),
    short_name: types.optional(types.string, ''),
    full_name: types.optional(types.string, ''),
    email: types.optional(types.maybeNull(types.string), null),
    position_name: types.optional(types.string, ''),
    other_provider_id: types.optional(types.string, ''),
    phone_numbers: types.optional(types.array(PhoneModelGtwy), []),
  })
  .actions((self) => ({
    setPhones(phones: IPhone[]) {
      self.phone_numbers = cast(phones);
    },
  }));

const OtherMemberModel = types.model({
  area: types.optional(AreaModel, {}),
  email: types.optional(types.maybeNull(types.string), null),
  first_name: types.optional(types.string, ''),
  last_name: types.optional(types.string, ''),
  patronymic_name: types.optional(types.maybeNull(types.string), null),
  phones: types.optional(types.array(PhoneModel), []),
  id: types.optional(types.string, ''),
  short_name: types.optional(types.string, ''),
  str_name: types.optional(types.string, ''),
});

const OtherPersonModel = types
  .model({
    _id: types.optional(types.string, ''),
    first_name: types.optional(types.string, ''),
    last_name: types.optional(types.string, ''),
    patronymic_name: types.optional(types.maybeNull(types.string), null),
    short_name: types.optional(types.string, ''),
    full_name: types.optional(types.string, ''),
    email: types.optional(types.maybeNull(types.string), null),
    phone_numbers: types.optional(types.array(PhoneModelGtwy), []),
  })
  .actions((self) => ({
    setPhones(phones: IPhone[]) {
      self.phone_numbers = cast(phones);
    },
  }));

const AddNewThirdPartyModal = types.model({
  show: types.optional(types.boolean, false),
  addOrganization: types.optional(types.boolean, false),
});

// Все необходимые сведения о заявителе
const ApplicantModel = types
  .model({
    loading: types.optional(ApplicantRequestsModel, {}),
    applicantType: types.optional(ApplicantTypeMST, 'tenant'),
    thirdPartyType: types.optional(ThirdPartyTypeMST, 'other_person'),
    members: types.optional(types.array(MemberModel), []),
    otherMember: types.optional(OtherMemberModel, {}),
    workers: types.optional(types.array(WorkerModel), []),
    allOtherProviders: types.optional(types.array(ProviderModelGtwy), []),
    allOtherEmployees: types.optional(types.array(OtherEmployeeModel), []),
    allOtherPersons: types.optional(types.array(OtherPersonModel), []),
    otherEmployeeByPhone: types.maybeNull(types.array(OtherEmployeeModel)), // Представители сторонней организации полученные по номеру тел.
    memberId: types.optional(types.string, ''),
    workerId: types.optional(types.string, ''),
    organizationId: types.optional(types.string, ''),
    legalId: types.optional(types.string, ''),
    physicalId: types.optional(types.string, ''),
  })
  .volatile(() => ({
    searchResultsGeneration: 0,
    searchResultsLoadingGeneration: 0,
  }))
  .views((self) => ({
    get selectedMember() {
      const selected = self.members.find(
        (member) => member.id === self.memberId
      );

      if (selected && selected.phones.length) {
        const lastPhone = selected.phones[selected.phones.length - 1];

        return {
          _id: self.memberId,
          _type: self.applicantType,
          phone_number: {
            _type: lastPhone.phone_type,
            number: lastPhone.code + lastPhone.number,
            add: lastPhone.add,
          },
        };
      }

      return { _id: self.memberId, _type: self.applicantType };
    },
    get selectedWorker() {
      const selected = self.workers.find(
        (worker) => worker.id === self.workerId
      );

      if (selected && selected.phones.length) {
        const lastPhone = selected.phones[selected.phones.length - 1];

        return {
          _id: self.workerId,
          _type: self.applicantType,
          phone_number: {
            _type: lastPhone.phone_type,
            number: lastPhone.code + lastPhone.number,
            add: lastPhone.add,
          },
        };
      }

      return { _id: self.workerId, _type: self.applicantType };
    },
    get selectedThirdPartyAccount(): { [key: string]: any } {
      const accountsList =
        self.thirdPartyType === 'other_employee'
          ? self.allOtherEmployees
          : self.allOtherPersons;

      const selectedAccount = cloneDeep(
        accountsList.find(
          (item) =>
            item._id ===
            (self.thirdPartyType === 'other_employee'
              ? self.legalId
              : self.physicalId)
        )!
      );

      const params: { [key: string]: any } = Object.assign(selectedAccount, {
        _type: self.thirdPartyType,
      });
      delete params?.legal_account;
      delete params?.physical_account;
      delete params?.service_workers;

      return params;
    },
    get isValidOtherTenant(): boolean {
      return (
        self.otherMember.last_name.length >= 2 &&
        self.otherMember.first_name.length >= 2 &&
        !!self.otherMember.phones &&
        self.otherMember.phones[0]?.code?.length === 3 &&
        self.otherMember.phones[0]?.number?.length === 7
      );
    },
  }))
  .actions((self) => ({
    setApplicantType(type: IApplicantType) {
      self.applicantType = type;
    },
    setThirdPartyType(type: IThirdPartyTypeGtwy) {
      self.thirdPartyType = type;
    },
    setSelectedId(
      key:
        | 'memberId'
        | 'workerId'
        | 'organizationId'
        | 'legalId'
        | 'physicalId',
      id: string
    ) {
      self[key] = id;
    },
    // Аналог того, что делает getWorkersByPhone (self.workers = cast(...)),
    // но без сетевого поиска — нужен, когда сотрудник уже точно известен
    // (см. initFromHouseChatMessage) и искать его по телефону не нужно и
    // рискованно: getWorkersByPhone взял бы первого сотрудника с таким же
    // рабочим номером, но это не обязательно тот, кто создаёт заявку.
    setWorkers(workers: IWorkerNew[]) {
      self.workers = cast(workers);
    },
    changeOtherMemberParams(key: IOtherMemberKeys, value: any) {
      if (key === 'phones') {
        const phone = splitPhone(value);

        if (!!self.otherMember.phones.length) {
          self.otherMember.phones[0].code = phone.code;
          self.otherMember.phones[0].number = phone.number;
        } else {
          self.otherMember.phones.push({
            add: null,
            added_by_tenant: false,
            code: phone.code,
            number: '',
            id: null,
            not_actual: false,
            phone_type: 'cell',
            str_number: null,
          });
        }
      } else {
        self.otherMember[key] = value;
      }
    },
    changeMemberRating(id: string, rating: { [key: string]: any }) {
      self.members.find((item: IMember) => item.id === id)?.setRating(rating);
    },
    editWorkersList(worker: IWorkerNew, phone: string) {
      const workerFind = !self.workers.find(
        (item: IWorkerNew) => item.id === worker.id
      );

      if (workerFind) {
        self.workers = self.workers.filter((work) =>
          work.phones.find((workerPhone) => workerPhone.str_number === phone)
        ) as any;
        self.workers.push(worker);
      }
    },
    clearMembers() {
      self.memberId = '';
      applySnapshot(self.loading.members, {});
      applySnapshot(self.members, []);
    },
    clearSearchResultsPreservingRequests() {
      self.searchResultsGeneration += 1;
      applySnapshot(self.members, []);
      applySnapshot(self.otherMember, {});
      applySnapshot(self.workers, []);
      applySnapshot(self.allOtherProviders, []);
      applySnapshot(self.allOtherEmployees, []);
      applySnapshot(self.allOtherPersons, []);
      self.otherEmployeeByPhone = null;
      self.memberId = '';
      self.workerId = '';
      self.organizationId = '';
      self.legalId = '';
      self.physicalId = '';

      Object.entries(self.loading).forEach(([key, value]) => {
        if (self.loading[key as keyof typeof self.loading].requestCount === 0) {
          value.clearStates();
        }
      });
    },
    invalidateSearchResultsForFullReset() {
      self.searchResultsGeneration += 1;
      self.searchResultsLoadingGeneration += 1;
    },
    getMembersByArea: flow(function* (
      areaId: string,
      other_tenants: boolean,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;
      const canApply = () =>
        generation === self.searchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.searchResultsLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.members.loadingStates();

        const response: { results: Array<{ members: IMember[] }> } =
          yield GetFamiliesByArea({
            account__area__id: areaId,
            other_tenants_include: other_tenants,
          });

        const members: IMember[] = response.results.flatMap(
          (item) => item.members
        );
        const uniqueMembers = uniqBy(members, 'id');

        if (canApply()) {
          self.memberId = uniqueMembers[0]?.id || '';
          self.members.replace(
            uniqueMembers.map((member) => MemberModel.create(member))
          );
          self.loading.members.successStates();
        }

        return members;
      } catch (e) {
        if (canApply()) {
          self.loading.members.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.members.finallyStates();
        }
      }
    }),
    getWorkersByPhone: flow(function* (
      value: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;
      const canApply = () =>
        generation === self.searchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.searchResultsLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.workers.loadingStates();

        const phone = splitPhone(value);
        const response: { results: IWorkerNew[] } =
          yield GetWorkersCardsByPhone({
            phones__str_number: phone.code + phone.number,
            is_dismiss__ne: true,
          });
        const workers = response.results.map((worker) => worker);

        if (canApply()) {
          if (workers.length) {
            self.workerId = workers[0]?.id;
          }

          self.workers = cast(workers);
          self.loading.workers.successStates();
        }

        return workers;
      } catch (e) {
        if (canApply()) {
          self.loading.workers.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.workers.finallyStates();
        }
      }
    }),
    getOtherPersonAccounts: flow(function* (phone: string) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;

      try {
        self.loading.otherPersonAccounts.loadingStates();

        const response: any = yield GetOtherPersonAccounts({
          phone_number: phone,
        });

        if (generation === self.searchResultsGeneration) {
          self.loading.otherPersonAccounts.successStates();
          self.allOtherPersons = response;
        }

        return response;
      } catch (e) {
        if (generation === self.searchResultsGeneration) {
          self.loading.otherPersonAccounts.errorStates();
          console.error(e);
        }
      } finally {
        if (loadingGeneration === self.searchResultsLoadingGeneration) {
          self.loading.otherPersonAccounts.finallyStates();
        }
      }
    }),
    getOtherEmployeeAccounts: flow(function* (
      provider_id: string,
      phone_number: string
    ) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;

      try {
        self.loading.otherEmployeeAccounts.loadingStates();

        const response = yield GetOtherEmployeeAccounts({
          provider_id,
          phone_number,
        });

        if (generation === self.searchResultsGeneration) {
          self.allOtherEmployees = response;
          self.loading.otherEmployeeAccounts.successStates();
        }

        return response;
      } catch (e) {
        if (generation === self.searchResultsGeneration) {
          self.loading.otherEmployeeAccounts.errorStates();
          console.error(e);
        }
      } finally {
        if (loadingGeneration === self.searchResultsLoadingGeneration) {
          self.loading.otherEmployeeAccounts.finallyStates();
        }
      }
    }),
    getOtherProvidersByPhone: flow(function* (strPhone: string) {
      const generation = self.searchResultsGeneration;
      const loadingGeneration = self.searchResultsLoadingGeneration;

      try {
        self.loading.otherProviderAccounts.loadingStates();
        const phone = splitPhone(strPhone);
        const response: any = yield GetOtherProvidersByPhone({
          phone_number: phone.code + phone.number,
        });

        if (generation === self.searchResultsGeneration) {
          self.loading.otherProviderAccounts.successStates();
          self.allOtherProviders = response;
        }

        return response;
      } catch (e) {
        if (generation === self.searchResultsGeneration) {
          self.loading.otherProviderAccounts.errorStates();
          console.error(e);
        }
      } finally {
        if (loadingGeneration === self.searchResultsLoadingGeneration) {
          self.loading.otherProviderAccounts.finallyStates();
        }
      }
    }),

    setApplicantContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const newParams = killNulls(params);
        const response: any = yield SetTenantContacts(id, newParams);

        let applicant;

        switch (self.applicantType) {
          case 'tenant':
            applicant = self.members.find(
              (item: IMember): boolean => item.id === id
            );
            break;

          case 'employee':
            applicant = self.workers.find(
              (item: IWorkerNew): boolean => item.id === id
            );
            break;

          default:
            break;
        }

        applicant?.setPhones(response.phones);

        self.loading.addContacts.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.loading.addContacts.errorStates();
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setEmployeeContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const newParams = killNulls(params);
        const response: any = yield SetEmployeeContacts(id, newParams);

        const worker = self.workers.find(
          (item: IWorkerNew): boolean => item.id === id
        );

        worker?.setPhones(response.phones);

        self.loading.addContacts.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setOtherPersonContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const response: any = yield SetOtherPersonContacts(id, params);

        const applicant = self.allOtherPersons.find(
          (item): boolean => item._id === id
        );
        applicant?.setPhones(response.phone_numbers);
        self.loading.addContacts.successStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
    setOtherEmployeeContacts: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addContacts.loadingStates();

        const response: any = yield SetOtherEmployeeContacts(id, params);

        const applicant = self.allOtherEmployees.find(
          (item): boolean => item._id === id
        );
        applicant?.setPhones(response.phone_numbers);
        self.loading.addContacts.errorStates();

        return response;
      } catch (error) {
        console.error(error);
        self.loading.addContacts.errorStates();

        return Promise.reject(error);
      } finally {
        self.loading.addContacts.finallyStates();
      }
    }),
  }));

export const CreateRequestStore = types
  .model({
    loading: types.optional(RequestsModel, {}),
    initialPhoneNumber: types.optional(types.string, ''),
    applicantSearchSource: types.optional(
      types.maybeNull(
        types.enumeration('ApplicantSearchSource', ['phone', 'address'])
      ),
      null
    ),
    applicantSearchStarted: types.optional(types.boolean, false),
    socialNetworks: types.optional(
      types.frozen<{ [propName: string]: any }>(),
      {}
    ),
    lastValidPhoneNumber: types.optional(types.string, ''),
    initialCallId: types.optional(types.maybeNull(types.string), null),
    activeTab: types.optional(TabTypeMST, 'applicant'),
    activeHistoryTab: types.optional(HistoryTabTypeMST, 'house'),
    applicant: types.optional(ApplicantModel, {}),
    areas: types.optional(types.array(AreaModel), []),
    manuallySelectedArea: types.optional(AreaModel, {}),
    manuallySelectedHouse: types.optional(HouseModel, {}),
    anotherAddress: types.optional(types.boolean, false),
    anotherMember: types.optional(types.boolean, false),
    addNewThirdPartyModal: types.optional(AddNewThirdPartyModal, {}),
    isNotify: types.optional(types.boolean, false),
    notifyText: types.optional(types.string, ''),
    currentSelectedCoordinators: types.optional(CurrentSelectedCoordinators, {
      departments: [],
      positions: [],
      workers: [],
    }),
    requestSyncCloneData: types.optional(types.frozen<INewRequest>(), {
      actions: [],
      status: 'accepted',
      status_history: [],
      area: {
        _id: '',
      },
      house: {
        _id: '',
      },
      description: '',
      execution: {
        desired_start_at: null,
        desired_end_at: null,
        coordinators: [],
      },
      is_public: false,
      administrative_supervision: false,
      housing_supervision: false,
      tag: 'current',
      category: '',
      subcategory: null,
      relations: {
        template_id: null,
        requests: [],
        call_ids: null,
        dupl_request_group_id: null,
      },
      work_area: null,
      _type: 'area',
    }),
    requestData: types.optional(types.frozen<INewRequest>(), {
      actions: [],
      status: 'accepted',
      status_history: [],
      area: {
        _id: '',
      },
      house: {
        _id: '',
      },
      description: '',
      execution: {
        desired_start_at: null,
        desired_end_at: null,
        coordinators: [],
      },
      is_public: false,
      administrative_supervision: false,
      housing_supervision: false,
      tag: 'current',
      category: '',
      subcategory: null,
      requester_attachment: { files: [], comment: '' },
      relations: {
        template_id: null,
        requests: [],
        call_ids: null,
        dupl_request_group_id: null,
      },
      work_area: null,
      _type: 'area',
    }),
  })
  .volatile(() => ({
    applicantSearchToken: 0,
    applicantSearchResultsGeneration: 0,
    applicantSearchLoadingGeneration: 0,
  }))
  .views((self) => ({
    get newRequestData(): INewRequest {
      const applicant: { [key: string]: any } =
        self.applicant.applicantType === 'tenant'
          ? { requester: self.applicant.selectedMember }
          : self.applicant.applicantType === 'employee'
            ? {
                requester: self.applicant.selectedWorker,
              }
            : {
                requester: self.applicant.selectedThirdPartyAccount,
              };

      return Object.assign(cloneDeep(self.requestData), {
        ...applicant,
        // Собирается в requestData, но id надо прихранить в случае смены типа заявки
        area: {
          _id:
            self.requestData._type === 'area'
              ? self.requestData.area?._id
              : null,
        },
      });
    },
    get selectedArea(): IArea {
      const selectedArea =
        self.areas.find(
          (item: IArea): boolean => item.id === self.requestData.area?._id
        ) || self.manuallySelectedArea;

      return cloneDeep(selectedArea);
    },
    get initialLoading(): boolean {
      return (
        self.loading.areas.pending ||
        self.applicant.loading.members.pending ||
        self.applicant.loading.workers.pending ||
        self.loading.areas.fulfilled ||
        self.applicant.loading.members.fulfilled ||
        self.applicant.loading.members.error ||
        self.applicant.loading.workers.fulfilled ||
        self.applicant.loading.thirdPartyAccountsByPhone.fulfilled
      );
    },
    isDisableTab(tab: TabType) {
      const isTenantSelected: boolean = self.anotherMember
        ? self.applicant.isValidOtherTenant
        : !!self.applicant.memberId;

      const isOtherSelected: boolean =
        self.applicant.thirdPartyType === 'other_person'
          ? !!self.applicant.physicalId
          : !!self.applicant.legalId;

      const isDescriptionAvailable: boolean =
        !!removeHtmlTags(self.requestData.description) &&
        !!self.requestData.category?.length;

      switch (tab) {
        case 'applicant':
          return false;
        case 'status':
        case 'meters':
          return self.applicant.applicantType !== 'tenant' || !isTenantSelected;
        case 'description':
          return (
            !self.requestData.house?._id ||
            (self.applicant.applicantType === 'employee'
              ? !self.applicant.workerId
              : self.applicant.applicantType === 'other'
                ? !isOtherSelected
                : !isTenantSelected)
          );
        case 'additionally':
          return (
            !isDescriptionAvailable ||
            (self.applicant.applicantType === 'employee'
              ? !self.applicant.workerId
              : self.applicant.applicantType === 'other'
                ? !isOtherSelected
                : !isTenantSelected)
          );
        default:
          return true;
      }
    },
    getFilteredActionsSelect(
      actionsBySubCategory: ISelectValue[],
      type?: string
    ) {
      if (!type) {
        return actionsBySubCategory.find(
          (item: ISelectValue) =>
            item.value === self.requestData.actions[0]?._type
        );
      }

      const actionByType = self.requestData.actions.find(
        (action) => action._type === type
      );

      return actionsBySubCategory.find(
        (item: ISelectValue) => item.value === actionByType?._type
      );
    },
  }))
  .actions((self) => ({
    setInitialCallId(id: string | null) {
      self.initialCallId = id;
    },
    setPhoneNumber(
      key: 'initialPhoneNumber' | 'lastValidPhoneNumber',
      phone: string
    ) {
      self[key] = phone;
    },
    setActiveTab(tab: TabType) {
      self.activeTab = tab;
    },
    setActiveHistoryTab(value: IHistoryTabType) {
      self.activeHistoryTab = value;
    },
    // applicantSearchStarted иначе выставляется только внутри
    // searchApplicantByPhone/getAreasByPhone-поиска (см. Applicant —
    // блок "Тип заявителя" скрыт, пока applicantSearchStarted === false).
    // Нужен, когда тип заявителя проставляется программно (не через
    // реальный поиск по телефону), чтобы этот блок сразу отрисовался.
    markApplicantSearchStarted() {
      self.applicantSearchStarted = true;
    },
    setRequestSyncCloneData() {
      self.requestSyncCloneData = cloneDeep(self.requestData);
    },
    setRequestData(data: INewRequest) {
      self.requestData = data;
    },
    setRequestCoordinators() {
      self.requestData = {
        ...self.requestData,
        execution: {
          ...self.requestData.execution,
          coordinators: [
            ...(self.requestData.execution.coordinators || []),
            ...self.currentSelectedCoordinators.workers.map((_id) => ({
              _id,
            })),
          ],
        },
      };
    },
    removeRequestCoordinator(delId: string) {
      self.requestData = {
        ...self.requestData,
        execution: {
          ...self.requestData.execution,
          coordinators: (self.requestData.execution.coordinators || []).filter(
            (coordinator) => coordinator._id !== delId
          ),
        },
      };
    },
    setBooleanProperties(
      key: 'anotherAddress' | 'anotherMember' | 'isNotify',
      bool: boolean
    ) {
      self[key] = bool;
    },
    setStringProperties(key: 'notifyText', value: string) {
      self[key] = value;
    },
    setAddNewThirdPartyModalProperties(
      key: 'show' | 'addOrganization',
      bool: boolean
    ) {
      self.addNewThirdPartyModal[key] = bool;
    },
    setManuallySelectedArea(area: IArea) {
      self.manuallySelectedArea = cast(area);
    },
    setManuallySelectedHouse(house: IHouse) {
      self.manuallySelectedHouse = cast(house);
    },
    clearTab(key: 'applicant') {
      applySnapshot(self[key], {});
    },
    clearSearchResultsPreservingRequests() {
      self.applicantSearchResultsGeneration += 1;
      self.socialNetworks = {};
      self.applicant.clearSearchResultsPreservingRequests();
      applySnapshot(self.areas, []);

      Object.entries(self.loading).forEach(([key, value]) => {
        if (self.loading[key as keyof typeof self.loading].requestCount === 0) {
          value.clearStates();
        }
      });
    },
  }))
  .actions((self) => ({
    clearCreateRequest(fullReset: boolean = false) {
      if (fullReset) {
        self.applicantSearchToken += 1;
        self.applicantSearchResultsGeneration += 1;
        self.applicantSearchLoadingGeneration += 1;
        self.socialNetworks = {};
        applySnapshot(self.loading, {});
        self.applicant.invalidateSearchResultsForFullReset();
        applySnapshot(self.applicant, {});
      } else {
        self.clearSearchResultsPreservingRequests();
      }

      self.activeTab = 'applicant';
      self.activeHistoryTab = 'house';
      applySnapshot(self.areas, []);
      applySnapshot(self.manuallySelectedHouse, {});
      applySnapshot(self.manuallySelectedArea, {});
      self.anotherAddress = false;
      self.anotherMember = false;
      applySnapshot(self.addNewThirdPartyModal, {});
      applySnapshot(self.currentSelectedCoordinators, {
        departments: [],
        positions: [],
        workers: [],
      });
      self.requestData = {
        actions: [],
        status: 'accepted',
        status_history: [],
        area: {
          _id: '',
        },
        house: {
          _id: '',
        },
        is_public: false,
        description: '',
        category: '',
        subcategory: null,
        execution: {
          desired_start_at: null,
          desired_end_at: null,
          coordinators: [],
        },
        administrative_supervision: false,
        housing_supervision: false,
        tag: 'current',
        relations: {
          template_id: null,
          requests: [],
          call_ids: null,
          dupl_request_group_id: null,
        },
        work_area: null,
        _type: 'area',
      };
      self.requestSyncCloneData = {
        actions: [],
        status: 'accepted',
        status_history: [],
        area: {
          _id: '',
        },
        house: {
          _id: '',
        },
        is_public: false,
        description: '',
        category: '',
        subcategory: null,
        execution: {
          desired_start_at: null,
          desired_end_at: null,
          coordinators: [],
        },
        administrative_supervision: false,
        housing_supervision: false,
        tag: 'current',
        relations: {
          template_id: null,
          requests: [],
          call_ids: null,
          dupl_request_group_id: null,
        },
        work_area: null,
        _type: 'area',
      };
      self.notifyText = '';

      if (fullReset) {
        self.initialPhoneNumber = '';
        self.lastValidPhoneNumber = '';
        self.initialCallId = null;
        self.applicantSearchSource = null;
        self.applicantSearchStarted = false;
      }
    },
    setNotifyTemplateText(action: string) {
      const dateFrom: string =
        moment(self.requestData.actions[0]?.start_at).format(
          'HH:mm DD.MM.YYYY'
        ) || '';
      const dateTill: string =
        moment(self.requestData.actions[0]?.end_at).format(
          'HH:mm DD.MM.YYYY'
        ) || '';
      const whatDisabled = action.replace('Отключить ', '');
      self.notifyText = `Уважаемые Жители! Уведомляем Вас, что с ${dateFrom} до ${dateTill} будет отключен ${whatDisabled}.\n\nПриносим извинения за доставленные неудобства.`;
    },
    uncheckRelatedRequest() {
      self.activeTab =
        self.activeTab === 'additionally' ? 'description' : self.activeTab;
      self.requestData = {
        ...self.requestData,
        actions: [],
      };
    },
    clearAction() {
      self.requestData = {
        ...self.requestData,
        actions: [],
      };
    },
    getSocialNetworksByPhone: flow(function* (
      phone: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      if (!/^\d{10}$/.test(phone)) {
        return;
      }

      const generation = self.applicantSearchResultsGeneration;
      const loadingGeneration = self.applicantSearchLoadingGeneration;
      const canApply = () =>
        generation === self.applicantSearchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.applicantSearchLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.socials.loadingStates();

        const response = yield GetNumberSocialNetworks({
          phone_number: phone,
        });

        if (canApply()) {
          self.socialNetworks = response;
          self.loading.socials.successStates();
        }

        return response;
      } catch (e) {
        if (canApply()) {
          self.loading.socials.errorStates();
          console.error(e);
        }
      } finally {
        if (canFinalize()) {
          self.loading.socials.finallyStates();
        }
      }
    }),
    getAreasByPhone: flow(function* (
      strPhone: string,
      shouldApply?: ApplicantSearchGuard,
      shouldFinalize?: ApplicantSearchGuard
    ) {
      const generation = self.applicantSearchResultsGeneration;
      const loadingGeneration = self.applicantSearchLoadingGeneration;
      const canApply = () =>
        generation === self.applicantSearchResultsGeneration &&
        (shouldApply?.() ?? true);
      const canFinalize = () =>
        loadingGeneration === self.applicantSearchLoadingGeneration &&
        (shouldFinalize?.() ?? true);

      try {
        self.loading.areas.loadingStates();

        const phone = splitPhone(strPhone);
        const response: { results: ITenant[] } = yield GetTenantsCardsByPhone({
          phones__str_number: phone.code + phone.number,
        });
        const areas = response.results.map((item) => {
          return item.area;
        });
        const uniqueAreas = uniqBy(areas, 'id');

        if (canApply()) {
          if (!!uniqueAreas.length) {
            self.requestData = Object.assign(cloneDeep(self.requestData), {
              area: { _id: uniqueAreas[0].id },
              house: { _id: uniqueAreas[0].house.id },
            });
          }

          self.areas = cast(uniqueAreas);

          self.loading.areas.successStates();
        }

        return uniqueAreas;
      } catch (e) {
        if (canApply()) {
          self.loading.areas.errorStates();
          console.error(e);
        }

        return [];
      } finally {
        if (canFinalize()) {
          self.loading.areas.finallyStates();
        }
      }
    }),
    createOtherMember: flow(function* (params: any) {
      try {
        self.loading.addOtherMember.loadingStates();

        const newParams = killNulls(params);
        const response: any = yield CreateOtherTenant(newParams);
        self.applicant.otherMember = response;
        self.applicant.memberId = response.id;

        self.loading.addOtherMember.successStates();

        return response;
      } catch (e) {
        self.loading.addOtherMember.errorStates();
        console.error(e);
      } finally {
        self.loading.addOtherMember.finallyStates();
      }
    }),
    createNewOtnerPerson: flow(function* (params: { [key: string]: any }) {
      try {
        self.loading.addNewOtherPerson.loadingStates();

        const prepareParam: { [key: string]: any } = {
          last_name: params.last_name,
          first_name: params.first_name,
          patronymic_name: params.patronymic_name ?? null,
          email: params.email || null,
          phone_numbers: [
            {
              _type: 'cell',
              number: params.phone,
            },
          ],
        };
        const response = yield CreateNewOtherPerson(prepareParam);

        self.loading.addNewOtherPerson.successStates();
        self.applicant.allOtherPersons.push(response);

        return response;
      } catch (e) {
        self.loading.addNewOtherEmployee.errorStates();
        console.error(e);
      } finally {
        self.loading.addNewOtherEmployee.finallyStates();
      }
    }),
    createNewOtherProvider: flow(function* (params: { [key: string]: any }) {
      try {
        self.loading.addNewOtherProvider.loadingStates();

        const prepareParam: { [key: string]: any } = {
          name: params.service_name,
          ...(params.inn ? { inn: params.inn } : {}),
          ...(params.ogrn ? { ogrn: params.ogrn } : {}),
        };

        const response = yield CreateNewOtherProvider(prepareParam);

        self.loading.addNewOtherProvider.successStates();

        self.applicant.allOtherProviders.push(response);
        self.applicant.otherEmployeeByPhone?.clear();

        return response;
      } catch (e) {
        self.loading.addNewOtherProvider.errorStates();
        console.error(e);
      } finally {
        self.loading.addNewOtherProvider.finallyStates();
      }
    }),
    createNewOtherEmployee: flow(function* (
      id: string,
      params: { [key: string]: any }
    ) {
      try {
        self.loading.addNewOtherEmployee.loadingStates();

        const prepareParam: { [key: string]: any } = {
          last_name: params.last_name,
          first_name: params.first_name,
          patronymic_name: params.patronymic_name ?? null,
          email: params.email || null,
          phone_numbers: [
            {
              _type: 'cell',
              number: params.phone,
            },
          ],
          position_name: params.position,
          other_provider_id: id,
        };
        const response = yield CreateNewOtherEmployee(prepareParam);

        self.loading.addNewOtherEmployee.successStates();
        self.applicant.allOtherEmployees.push(response);

        return response;
      } catch (e) {
        self.loading.addNewOtherEmployee.errorStates();
        console.error(e);
      } finally {
        self.loading.addNewOtherEmployee.finallyStates();
      }
    }),
    createRequest: flow(function* (data: INewRequest) {
      try {
        self.loading.create.loadingStates();

        const response: IRequest = yield CreateRequest(data);

        self.loading.create.successStates();

        return response;
      } catch (e: any) {
        self.loading.create.errorStates();
        console.error(e);

        throw e;
      } finally {
        self.loading.create.finallyStates();
      }
    }),
    postShutdownNotification: flow(function* (
      porches: string[],
      address: string
    ) {
      try {
        self.loading.notify.loadingStates();

        const response = yield PostShutdownNotification({
          porches,
          address,
          text: self.notifyText,
        });

        self.loading.notify.successStates();

        return response;
      } catch (e) {
        console.error(e);
        self.loading.notify.errorStates();
      } finally {
        self.loading.notify.finallyStates();
      }
    }),
    /** Заполняет заявку под сценарий "создание из сообщения домового чата
     * от лица сотрудника": тип заявителя "employee" с сразу выбранным
     * работником и домовая заявка с описанием из текста сообщения.
     * employee передаётся целиком (а не просто employeeId/employeePhone и
     * поиск getWorkersByPhone) — сотрудник тут уже точно известен (это сам
     * залогиненный пользователь), а поиск по телефону мог бы подставить
     * другого человека с тем же рабочим номером. */
    initFromHouseChatMessage(params: {
      houseId: string;
      description: string;
      employee: IWorkerNew;
      messageId: string;
      chatId: number;
    }) {
      self.applicant.setApplicantType('employee');
      self.applicant.setWorkers([params.employee]);
      self.applicant.setSelectedId('workerId', params.employee.id);
      self.setActiveHistoryTab('house');
      self.markApplicantSearchStarted();

      self.setRequestData({
        _type: 'house',
        area: null,
        house: {
          _id: params.houseId,
        },
        requester: {
          _id: params.employee.id,
          _type: 'employee',
        },
        execution: {
          desired_start_at: null,
          desired_end_at: null,
          coordinators: [],
        },
        is_public: false,
        administrative_supervision: false,
        housing_supervision: false,
        status: 'accepted',
        description: params.description,
        category: '',
        subcategory: null,
        work_area: null,
        actions: [],
        tag: 'current',
        requester_attachment: {
          files: [],
          comment: '',
        },
        relations: {
          template_id: null,
          requests: [],
          call_ids: null,
          dupl_request_group_id: null,
        },
        source: 'house_chat',
        house_chat: {
          chat_id: params.chatId,
          max_message_id: params.messageId,
        },
      });
    },
  }))
  .actions((self) => ({
    invalidateApplicantSearch(source: 'phone' | 'address') {
      self.applicantSearchSource = source;
      self.applicantSearchStarted = true;
      self.applicantSearchToken += 1;
      self.clearSearchResultsPreservingRequests();
    },
    setPhoneFromApplicant(phone: string) {
      self.initialPhoneNumber = phone;
    },
  }))
  .actions((self) => ({
    searchApplicantByAddress: flow(function* (
      areaId: string,
      context: ApplicantSearchContext = {}
    ) {
      self.applicantSearchSource = 'address';
      self.applicantSearchStarted = true;
      self.applicantSearchToken += 1;
      self.clearSearchResultsPreservingRequests();

      const searchToken = self.applicantSearchToken;
      const loadingGeneration = self.applicantSearchLoadingGeneration;
      const isCurrent = () => searchToken === self.applicantSearchToken;
      const shouldFinalize = () =>
        loadingGeneration === self.applicantSearchLoadingGeneration;
      const members: IMember[] | undefined =
        yield self.applicant.getMembersByArea(
          areaId,
          true,
          isCurrent,
          shouldFinalize
        );

      if (!isCurrent()) {
        return members;
      }

      const selectedMember = members?.[0];

      if (selectedMember) {
        // const phone = getPreferredApplicantPhone(selectedMember.phones);

        // if (phone) {
        //   self.setPhoneFromApplicant(phone);
        // }

        if (selectedMember.id) {
          safelyInvoke(
            context.onTenantSelected,
            selectedMember.id,
            areaId,
            isCurrent
          );
        }
      } else if (self.applicant.loading.members.error) {
        self.setBooleanProperties('anotherMember', true);
      }

      return members;
    }),
    searchApplicantByPhone: flow(function* (
      phone: string,
      context: ApplicantSearchContext = {}
    ) {
      self.applicantSearchSource = 'phone';
      self.applicantSearchStarted = true;
      self.applicantSearchToken += 1;

      const searchToken = self.applicantSearchToken;
      const loadingGeneration = self.applicantSearchLoadingGeneration;
      const isCurrent = () => searchToken === self.applicantSearchToken;
      const shouldFinalize = () =>
        loadingGeneration === self.applicantSearchLoadingGeneration;

      self.clearCreateRequest();
      context.clearRelatedData?.();
      self.setPhoneNumber('lastValidPhoneNumber', phone);
      safelyInvoke(context.searchSocialNetworks, phone, isCurrent);

      const areas: IArea[] | undefined = yield self.getAreasByPhone(
        phone,
        isCurrent,
        shouldFinalize
      );

      if (!isCurrent()) {
        return;
      }

      const firstArea = areas?.[0];

      self.applicant.setApplicantType('employee');
      const workersSearch = self.applicant.getWorkersByPhone(
        phone,
        isCurrent,
        shouldFinalize
      );

      if (firstArea?.house.id) {
        safelyInvoke(context.loadHouse, firstArea.house.id, isCurrent);
      }

      const workers: IWorkerNew[] | undefined = yield workersSearch;

      if (!isCurrent()) {
        return;
      }

      if (firstArea) {
        self.applicant.setApplicantType('tenant');
        const members: IMember[] | undefined =
          yield self.applicant.getMembersByArea(
            firstArea.id,
            true,
            isCurrent,
            shouldFinalize
          );

        if (!isCurrent()) {
          return;
        }

        const selectedMember = members?.[0];

        if (selectedMember?.id) {
          safelyInvoke(
            context.onTenantSelected,
            selectedMember.id,
            firstArea.id,
            isCurrent
          );
        }
      } else if (!workers?.length) {
        self.applicant.setApplicantType('tenant');
      }
    }),
  }))
  .actions((self) => ({
    handlePhoneInput: flow(function* (
      phone: string,
      context: ApplicantSearchContext = {}
    ) {
      self.initialPhoneNumber = phone;

      const isInvalidPhone = !/^\d{10}$/.test(phone);
      const isRepeatedPhoneSearch =
        phone === self.lastValidPhoneNumber &&
        self.applicantSearchSource === 'phone';

      if (isRepeatedPhoneSearch) {
        return;
      }

      if (isInvalidPhone) {
        self.applicantSearchSource = 'phone';
        self.applicantSearchToken += 1;
        self.lastValidPhoneNumber = '';

        return;
      }

      yield self.searchApplicantByPhone(phone, context);
    }),
  }));
